import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from 'recharts';
import {
    accountingPnlCargoOwnerRow,
    accountingPnlRange,
    accountingPnlSummaryResponse
} from '../../../models/entities/businessPartner/accountingPnl';
import { PagesRoutes } from '../../../routing/PagesRoutes';
import { localSDK as client } from '../../../sdk';
import { DropDown } from '../../shared/inputs/base';
import Loader from '../../shared/SmallComponents/Loader';
import { formatDate, formatMoney } from '../businessPartners/singleBusinessPartner/sections/Accounting/format';
import StatusPill from '../businessPartners/singleBusinessPartner/sections/Accounting/statusPill';
import {
    ChartCard,
    ChartTitle,
    EmptyHint,
    EmptyTd,
    FilterField,
    KpiLabel,
    KpiSub,
    KpiValue,
    Section,
    SectionHeader,
    SectionTitle,
    ShipmentLink,
    Table,
    TableCard,
    Td,
    Th,
    Tr
} from './AccountingOversightStyles';
import { styled } from '../../shared/Theme/theme';

const RANGE_OPTIONS: Array<{ value: accountingPnlRange; text: string }> = [
    { value: 'all', text: 'All Time' },
    { value: '30d', text: 'Last 30 Days' },
    { value: '60d', text: 'Last 60 Days' },
    { value: '90d', text: 'Last 90 Days' },
    { value: '180d', text: 'Last 6 Months' },
    { value: '1y', text: 'Last 1 Year' }
];

const safeMoney = (amount: unknown, currency: string): string => {
    const n = typeof amount === 'number' ? amount : Number(amount);
    if (!Number.isFinite(n)) return formatMoney(0, currency);
    return formatMoney(n, currency, { decimals: 'auto' });
};

const marginPct = (revenue: number, profit: number): string | null => {
    if (!Number.isFinite(revenue) || revenue <= 0 || !Number.isFinite(profit)) return null;
    return `${((profit / revenue) * 100).toFixed(1)}%`;
};

type Props = {
    displayCurrency?: string;
};

const CargoOwnerPnlSection = ({ displayCurrency = 'USD' }: Props) => {
    const navigate = useNavigate();
    const [range, setRange] = useState<accountingPnlRange>('90d');
    const [data, setData] = useState<accountingPnlSummaryResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [expandedOwnerId, setExpandedOwnerId] = useState<string | null>(null);
    const [ownerSearch, setOwnerSearch] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await client.businessPartners().accounting().fetchPnlSummary({
                range,
                displayCurrency
            });
            setData(res);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Failed to load profit & loss');
            setData(null);
        } finally {
            setLoading(false);
        }
    }, [range, displayCurrency]);

    useEffect(() => {
        void load();
    }, [load]);

    const currency = data?.displayCurrency ?? displayCurrency;
    const totals = data?.totals;

    const rows = useMemo(() => {
        const all = data?.byCargoOwner ?? [];
        const q = ownerSearch.trim().toLowerCase();
        if (!q) return all;
        return all.filter((o) => o.cargoOwnerName.toLowerCase().includes(q));
    }, [data, ownerSearch]);

    const toggleOwner = (owner: accountingPnlCargoOwnerRow) => {
        setExpandedOwnerId((prev) => (prev === owner.cargoOwnerId ? null : owner.cargoOwnerId));
    };

    const profitTone = (profit: number): 'danger' | 'success' | 'info' => {
        if (profit < 0) return 'danger';
        if (profit > 0) return 'success';
        return 'info';
    };

    const chartData = useMemo(
        () =>
            rows
                .filter((o) => o.shipmentCount > 0)
                .slice()
                .sort((a, b) => b.profit - a.profit)
                .slice(0, 12)
                .map((o) => ({
                    name:
                        o.cargoOwnerName.length > 18
                            ? `${o.cargoOwnerName.slice(0, 16)}…`
                            : o.cargoOwnerName,
                    fullName: o.cargoOwnerName,
                    profit: o.profit,
                    revenue: o.revenue,
                    cost: o.cost
                })),
        [rows]
    );

    return (
        <Section>
            <SectionHeader>
                <HeaderLeft>
                    <SectionTitle>P&amp;L analysis</SectionTitle>
                    <SectionSubtitle>
                        Company-wide profit and loss across cargo owners for the selected period. Expand a row to
                        drill into shipments.
                    </SectionSubtitle>
                </HeaderLeft>
            </SectionHeader>

            <Toolbar>
                <FilterField>
                    <DropDown
                        value={range}
                        onChange={(v) => setRange(v as accountingPnlRange)}
                        options={RANGE_OPTIONS}
                        placeHolder="Period"
                    />
                </FilterField>
                <SearchInput
                    type="search"
                    value={ownerSearch}
                    onChange={(e) => setOwnerSearch(e.target.value)}
                    placeholder="Search cargo owner…"
                    aria-label="Search cargo owner"
                />
                <CurrencyBadge>
                    Display currency <strong>{currency}</strong>
                </CurrencyBadge>
            </Toolbar>

            {loading && (
                <LoadingWrap>
                    <Loader />
                </LoadingWrap>
            )}
            {error && <EmptyHint>{error}</EmptyHint>}

            {!loading && data && totals && (
                <SummaryRow>
                    <SummaryCard>
                        <KpiLabel>Total revenue</KpiLabel>
                        <KpiValue $tone="info">{safeMoney(totals.revenue, currency)}</KpiValue>
                        <KpiSub>
                            {totals.cargoOwnerCount} cargo owner{totals.cargoOwnerCount === 1 ? '' : 's'}
                        </KpiSub>
                    </SummaryCard>
                    <SummaryCard>
                        <KpiLabel>Total cost</KpiLabel>
                        <KpiValue $tone="warn">{safeMoney(totals.cost, currency)}</KpiValue>
                        <KpiSub>
                            {totals.shipmentCount} shipment{totals.shipmentCount === 1 ? '' : 's'}
                        </KpiSub>
                    </SummaryCard>
                    <SummaryCard>
                        <KpiLabel>Total profit</KpiLabel>
                        <KpiValue $tone={profitTone(totals.profit)}>{safeMoney(totals.profit, currency)}</KpiValue>
                        <KpiSub>
                            {marginPct(totals.revenue, totals.profit)
                                ? `Margin ${marginPct(totals.revenue, totals.profit)}`
                                : 'After cargo-owner surcharge'}
                        </KpiSub>
                    </SummaryCard>
                </SummaryRow>
            )}

            {!loading && data && chartData.length > 0 && (
                <ChartCard style={{ marginBottom: 12 }}>
                    <ChartTitle>Profit by cargo owner</ChartTitle>
                    <ResponsiveContainer width="100%" height={Math.max(180, chartData.length * 28)}>
                        <BarChart
                            layout="vertical"
                            data={chartData}
                            margin={{ top: 4, right: 16, left: 4, bottom: 4 }}
                        >
                            <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#ececec" />
                            <XAxis
                                type="number"
                                tick={{ fontSize: 11, fill: '#6b7280' }}
                                tickFormatter={(v: number) => safeMoney(v, currency)}
                            />
                            <YAxis
                                type="category"
                                dataKey="name"
                                width={120}
                                tick={{ fontSize: 11, fill: '#6b7280' }}
                            />
                            <Tooltip
                                formatter={(value: number) => safeMoney(value, currency)}
                                labelFormatter={(_, payload) =>
                                    (payload?.[0]?.payload as { fullName?: string } | undefined)?.fullName || ''
                                }
                                contentStyle={{ borderRadius: 8, border: '1px solid #ececec', fontSize: 12 }}
                            />
                            <Bar dataKey="profit" name="Profit" radius={[0, 4, 4, 0]}>
                                {chartData.map((entry) => (
                                    <Cell
                                        key={entry.fullName}
                                        fill={entry.profit < 0 ? '#ef4444' : '#15803d'}
                                    />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </ChartCard>
            )}

            {!loading && data && (
                <TableCard>
                    <TableScroll>
                        <Table>
                            <thead>
                                <tr>
                                    <Th style={{ width: 44 }} aria-label="Expand" />
                                    <Th>Cargo owner</Th>
                                    <Th $center>Shipments</Th>
                                    <Th $right>Revenue</Th>
                                    <Th $right>Cost</Th>
                                    <Th $right>Profit</Th>
                                    <Th $right>Margin</Th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.length === 0 && (
                                    <tr>
                                        <EmptyTd colSpan={7}>
                                            {ownerSearch.trim()
                                                ? 'No cargo owners match your search for this period.'
                                                : 'No billed shipments in this period. Try a wider date range.'}
                                        </EmptyTd>
                                    </tr>
                                )}
                                {rows.map((owner) => {
                                    const expanded = expandedOwnerId === owner.cargoOwnerId;
                                    const m = marginPct(owner.revenue, owner.profit);
                                    return (
                                        <React.Fragment key={owner.cargoOwnerId}>
                                            <OwnerRow
                                                $expanded={expanded}
                                                onClick={() => toggleOwner(owner)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        e.preventDefault();
                                                        toggleOwner(owner);
                                                    }
                                                }}
                                                tabIndex={0}
                                                role="button"
                                                aria-expanded={expanded}
                                            >
                                                <Td>
                                                    <ExpandIcon className="material-icons" aria-hidden>
                                                        {expanded ? 'expand_more' : 'chevron_right'}
                                                    </ExpandIcon>
                                                </Td>
                                                <Td>
                                                    <OwnerName>{owner.cargoOwnerName}</OwnerName>
                                                    <OwnerHint>
                                                        {expanded ? 'Hide shipments' : 'Show shipments'}
                                                    </OwnerHint>
                                                </Td>
                                                <Td $center>
                                                    <CountBadge>{owner.shipmentCount}</CountBadge>
                                                </Td>
                                                <Td $right>{safeMoney(owner.revenue, currency)}</Td>
                                                <Td $right>{safeMoney(owner.cost, currency)}</Td>
                                                <Td $right>
                                                    <ProfitTone $negative={owner.profit < 0}>
                                                        {safeMoney(owner.profit, currency)}
                                                    </ProfitTone>
                                                </Td>
                                                <Td $right>
                                                    <MutedCell>{m ?? '—'}</MutedCell>
                                                </Td>
                                            </OwnerRow>
                                            {expanded &&
                                                (owner.shipments.length === 0 ? (
                                                    <tr>
                                                        <EmptyTd colSpan={7}>
                                                            No shipments with billing lines for this owner.
                                                        </EmptyTd>
                                                    </tr>
                                                ) : (
                                                    <>
                                                        <ChildHeaderRow>
                                                            <Td />
                                                            <Td colSpan={2}>
                                                                <ChildHeaderLabel>Shipment</ChildHeaderLabel>
                                                            </Td>
                                                            <Td $right>
                                                                <ChildHeaderLabel>Revenue</ChildHeaderLabel>
                                                            </Td>
                                                            <Td $right>
                                                                <ChildHeaderLabel>Cost</ChildHeaderLabel>
                                                            </Td>
                                                            <Td $right>
                                                                <ChildHeaderLabel>Profit</ChildHeaderLabel>
                                                            </Td>
                                                            <Td $right>
                                                                <ChildHeaderLabel>Status</ChildHeaderLabel>
                                                            </Td>
                                                        </ChildHeaderRow>
                                                        {owner.shipments.map((s) => (
                                                            <ChildRow key={s.shipmentId}>
                                                                <Td />
                                                                <Td colSpan={2}>
                                                                    <ShipmentCell>
                                                                        <ShipmentLink
                                                                            type="button"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                navigate(
                                                                                    `${PagesRoutes.Shipments}/${s.shipmentId}`
                                                                                );
                                                                            }}
                                                                        >
                                                                            {s.shipmentNumber || '—'}
                                                                        </ShipmentLink>
                                                                        <ShipmentMeta>
                                                                            {formatDate(s.shipmentDate)}
                                                                        </ShipmentMeta>
                                                                    </ShipmentCell>
                                                                </Td>
                                                                <Td $right>{safeMoney(s.revenue, currency)}</Td>
                                                                <Td $right>{safeMoney(s.cost, currency)}</Td>
                                                                <Td $right>
                                                                    <ProfitTone $negative={s.profit < 0}>
                                                                        {safeMoney(s.profit, currency)}
                                                                    </ProfitTone>
                                                                </Td>
                                                                <Td $right>
                                                                    <StatusPillWrap>
                                                                        <StatusPill code={s.accountingState} />
                                                                    </StatusPillWrap>
                                                                </Td>
                                                            </ChildRow>
                                                        ))}
                                                    </>
                                                ))}
                                        </React.Fragment>
                                    );
                                })}
                                {rows.length > 0 && totals && !ownerSearch.trim() && (
                                    <TotalsRow>
                                        <Td />
                                        <Td>
                                            <strong>All cargo owners</strong>
                                        </Td>
                                        <Td $center>
                                            <strong>{totals.shipmentCount}</strong>
                                        </Td>
                                        <Td $right>
                                            <strong>{safeMoney(totals.revenue, currency)}</strong>
                                        </Td>
                                        <Td $right>
                                            <strong>{safeMoney(totals.cost, currency)}</strong>
                                        </Td>
                                        <Td $right>
                                            <strong>
                                                <ProfitTone $negative={totals.profit < 0}>
                                                    {safeMoney(totals.profit, currency)}
                                                </ProfitTone>
                                            </strong>
                                        </Td>
                                        <Td $right>
                                            <strong>{marginPct(totals.revenue, totals.profit) ?? '—'}</strong>
                                        </Td>
                                    </TotalsRow>
                                )}
                            </tbody>
                        </Table>
                    </TableScroll>
                </TableCard>
            )}
        </Section>
    );
};

const HeaderLeft = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    max-width: 640px;
`;

const SectionSubtitle = styled.p`
    margin: 0;
    font-size: 13px;
    line-height: 1.4;
    color: #6b7280;
    font-weight: 400;
`;

const Toolbar = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    margin-bottom: 12px;
    padding: 10px 12px;
    background: #fff;
    border: 1px solid #ececec;
    border-radius: 10px;
`;

const SearchInput = styled.input`
    flex: 1;
    min-width: 180px;
    max-width: 280px;
    height: 36px;
    padding: 0 12px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    font-size: 13px;
    color: #374151;
    background: #fff;

    &::placeholder {
        color: #9ca3af;
    }

    &:focus {
        outline: none;
        border-color: #93c5fd;
        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.12);
    }
`;

const CurrencyBadge = styled.div`
    margin-left: auto;
    font-size: 12px;
    color: #6b7280;
    padding: 6px 10px;
    background: #f9fafb;
    border: 1px solid #ececec;
    border-radius: 8px;
    white-space: nowrap;

    strong {
        color: #374151;
        font-weight: 600;
    }
`;

const LoadingWrap = styled.div`
    padding: 24px 0;
`;

const SummaryRow = styled.div`
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;
    margin-bottom: 12px;

    @media (max-width: 800px) {
        grid-template-columns: 1fr;
    }
`;

const SummaryCard = styled.div`
    border-radius: 10px;
    padding: 14px 16px;
    min-height: 88px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    background: #fff;
    border: 1px solid #ececec;
    box-shadow: 0 1px 2px rgba(20, 30, 60, 0.04);
`;

const TableScroll = styled.div`
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
`;

const ExpandIcon = styled.span`
    font-size: 20px !important;
    color: #9ca3af;
    vertical-align: middle;
    line-height: 1;
`;

const OwnerName = styled.div`
    font-weight: 600;
    color: ${(p) => p.theme.colors.sectionTitle || '#1f2937'};
    font-size: 13px;
`;

const OwnerHint = styled.div`
    font-size: 11px;
    color: #9ca3af;
    margin-top: 2px;
`;

const CountBadge = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 28px;
    padding: 2px 8px;
    border-radius: 999px;
    background: #f3f4f6;
    color: #4b5563;
    font-size: 12px;
    font-weight: 600;
`;

const ProfitTone = styled.span<{ $negative?: boolean }>`
    color: ${(p) => (p.$negative ? '#c0392b' : '#15803d')};
    font-variant-numeric: tabular-nums;
`;

const MutedCell = styled.span`
    color: #6b7280;
    font-variant-numeric: tabular-nums;
`;

const OwnerRow = styled(Tr)<{ $expanded?: boolean }>`
    cursor: pointer;
    background: ${(p) => (p.$expanded ? '#f8fafc' : undefined)};

    &:hover {
        background: #f3f6fb;
    }

    &:focus-visible {
        outline: 2px solid #93c5fd;
        outline-offset: -2px;
    }
`;

const ChildHeaderRow = styled.tr`
    background: #f9fafb;

    td {
        padding-top: 8px;
        padding-bottom: 8px;
        border-bottom: 1px solid #ececec;
    }
`;

const ChildHeaderLabel = styled.span`
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.02em;
    text-transform: uppercase;
    color: #9ca3af;
`;

const ChildRow = styled(Tr)`
    background: #fcfcfd;

    td {
        font-size: 12px;
    }

    &:hover {
        background: #f5f7fa;
    }
`;

const ShipmentCell = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding-left: 8px;
    border-left: 2px solid #e5e7eb;
`;

const ShipmentMeta = styled.span`
    font-size: 11px;
    color: #9ca3af;
`;

const StatusPillWrap = styled.div`
    display: flex;
    justify-content: flex-end;
`;

const TotalsRow = styled(Tr)`
    background: #f3f4f6;

    td {
        border-top: 1px solid #e5e7eb;
        border-bottom: none;
        font-size: 13px;
    }

    &:hover {
        background: #f3f4f6;
    }
`;

export default CargoOwnerPnlSection;
